import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import type React from "react";
import { matchPath, useLocation } from "react-router";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import { CountdownBadge } from "@/components/Badge/CountdownBadge";
import type { IconComponent } from "@/components/Icon/Icon";
import { AnnotationQuestionIcon } from "@/components/Icons/AnnotationQuestionIcon";
import { Building02Icon } from "@/components/Icons/Building02Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { File04Icon } from "@/components/Icons/File04Icon";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { HorizontalBarChart01Icon } from "@/components/Icons/HorizontalBarChart01Icon";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { getCommunitySurveyCtaLabel } from "@/helpers/community-feed";
import type { SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { useEventQueries } from "@/queries/events/queryOptions";
import { usePollQueries } from "@/queries/polls/queryOptions";
import { useServiceQueries } from "@/queries/services/queryOptions";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EntityPreviewCard, EntityPreviewCardError, EntityPreviewCardSkeleton } from "./EntityPreviewCard";
import {
  EntityPreviewCardWithBackground,
  EntityPreviewCardWithBackgroundError,
  EntityPreviewCardWithBackgroundSkeleton,
} from "./EntityPreviewCardWithBackground";
import {
  EntityPreviewCardWithCustomAction,
  EntityPreviewCardWithCustomActionError,
  EntityPreviewCardWithCustomActionSkeleton,
} from "./EntityPreviewCardWithCustomAction";
import { EventPreviewCard } from "./EventPreviewCard";

type BaseEntityPreviewProps = {
  entityId: string;
  shouldLoad?: boolean;
  errorLabels?: {
    title: string;
    subtitle: string;
  };
  readOnly?: boolean;
  onClick?: () => void;
};

function EventPreview({
  entityId: eventId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const slug = useSlug();

  const eventQueries = useEventQueries();
  const {
    data: eventDetails,
    isPending: isPendingEventDetails,
    error: errorEventDetails,
  } = useQuery({
    ...eventQueries.details({
      eventId,
    }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorEventDetails) {
    return <EntityPreviewCardWithBackgroundError {...errorLabels} />;
  }

  if (isPendingEventDetails) {
    return <EntityPreviewCardWithBackgroundSkeleton />;
  }

  return (
    <EventPreviewCard
      image={eventDetails.image}
      fallbackImage={sessionUser.project.backgroundImage}
      href={readOnly ? undefined : routes.events.details({ slug, id: eventDetails.id })}
      onClick={readOnly ? undefined : onClick}
      title={eventDetails.name}
      startsAt={eventDetails.startsAt}
      endsAt={eventDetails.endsAt}
      location={eventDetails.location}
    />
  );
}

function GroupPreview({
  entityId: groupId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();
  const sessionUser = useSessionUser();

  const communityGroupQueries = useCommunityGroupQueries();
  const {
    data: groupDetails,
    isPending: isPendingGroupDetails,
    error: errorGroupDetails,
  } = useQuery({
    ...communityGroupQueries.details({
      groupId,
    }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorGroupDetails) {
    return <EntityPreviewCardWithBackgroundError {...errorLabels} />;
  }

  if (isPendingGroupDetails) {
    return <EntityPreviewCardWithBackgroundSkeleton />;
  }

  const href =
    groupDetails.type === "helpCategory"
      ? routes.helpCategories.details({ slug, id: groupDetails.id })
      : routes.interestGroups.details({ slug, id: groupDetails.id });

  return (
    <EntityPreviewCardWithBackground
      headerBackgroundImage={groupDetails.image || sessionUser.project.backgroundImage}
      href={readOnly ? undefined : href}
      onClick={readOnly ? undefined : onClick}
      title={groupDetails.name}
      subtitle={groupDetails.description}
    />
  );
}

function ServicePreview({
  entityId: serviceId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const resolveImage = useImageResolver();

  const servicePartnerQueries = useServiceQueries();
  const {
    data: serviceDetails,
    isPending: isPendingServiceDetails,
    error: errorServiceDetails,
  } = useQuery({
    ...servicePartnerQueries.details({ serviceId }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorServiceDetails) {
    return <EntityPreviewCardWithBackgroundError {...errorLabels} />;
  }

  if (isPendingServiceDetails) {
    return <EntityPreviewCardWithBackgroundSkeleton />;
  }

  return (
    <EntityPreviewCardWithBackground
      headerBackgroundImage={serviceDetails.headerImage || sessionUser.project.backgroundImage}
      href={readOnly ? undefined : routes.servicePartners.details({ slug, id: serviceDetails.id })}
      onClick={readOnly ? undefined : onClick}
      title={serviceDetails.title}
      subtitle={serviceDetails.subtitle}
      renderHeader={() =>
        serviceDetails.profileImage ? (
          <section className="flex flex-1 items-center justify-center">
            <img
              src={resolveImage(serviceDetails.profileImage, "lg")}
              className="m-space-md size-thumbnail-prominent overflow-hidden rounded-full border-4 border-background-default bg-background-default object-cover"
              alt={serviceDetails.title}
            />
          </section>
        ) : null
      }
    />
  );
}

function BookableAssetPreview({
  entityId: assetId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();

  const bookableAssetQueries = useBookableAssetsQueries();
  const {
    data: assetDetails,
    isPending: isPendingAssetDetails,
    error: errorLoadingAssetDetails,
  } = useQuery({
    ...bookableAssetQueries.details({ assetId }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorLoadingAssetDetails) {
    return <EntityPreviewCardError {...errorLabels} />;
  }

  if (isPendingAssetDetails) {
    return <EntityPreviewCardSkeleton />;
  }

  return (
    <EntityPreviewCard
      href={readOnly ? undefined : routes.bookings.bookAsset({ slug, aid: assetDetails.id })}
      onClick={readOnly ? undefined : onClick}
      title={assetDetails.name}
      image={assetDetails.images[0]}
    />
  );
}

function PracticalAndMoreDocumentPreview({
  entityId: documentId,
  folderId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps & { folderId: string }): React.ReactNode {
  const slug = useSlug();

  const documentQueries = useDocumentQueries();
  const {
    data: documentDetails,
    isPending: isPendingDocumentDetails,
    error: errorDocumentDetails,
  } = useQuery({
    ...documentQueries.fileDetails({ fileId: documentId, folderId: folderId }),
    retry: 1,
    enabled: shouldLoad,
  });
  const { data: folderDetails } = useQuery({
    ...documentQueries.listDocuments({ folderId: folderId }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorDocumentDetails) {
    return <EntityPreviewCardError {...errorLabels} />;
  }

  if (isPendingDocumentDetails) {
    return <EntityPreviewCardSkeleton />;
  }

  return (
    <EntityPreviewCard
      href={readOnly ? undefined : routes.documents.documentsList({ slug, folderId })}
      onClick={readOnly ? undefined : onClick}
      title={documentDetails.name}
      icon={documentDetails.type === "link" ? <Globe02Icon /> : <File04Icon />}
      subtitle={documentDetails.section?.name || folderDetails?.name}
    />
  );
}

function CommunityPostPreview({
  entityId: messageId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: messageDetails,
    isPending: isPendingMessageDetails,
    error: errorMessageDetails,
  } = useQuery({
    ...communityFeedQueries.messageDetails(messageId),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorMessageDetails) {
    return <EntityPreviewCardError {...errorLabels} />;
  }

  if (isPendingMessageDetails) {
    return <EntityPreviewCardSkeleton />;
  }

  const commonProps = {
    href: readOnly ? undefined : routes.communityFeed.postDetails({ slug, id: messageDetails.id }),
    onClick: readOnly ? undefined : onClick,
    title: messageDetails.title || "",
    renderSubtitle: () => (
      <Inline gap="xs">
        <UserAvatar size="xs" image={messageDetails.author.avatar} />
        <span className="text-caption text-text-secondary">{messageDetails.author.fullName}</span>
      </Inline>
    ),
  };
  let Icon: IconComponent;
  switch (messageDetails.messageLabel) {
    case "inform":
      Icon = <Edit05Icon />;
      break;

    case "sellItem":
      Icon = <Building02Icon />;
      break;

    case "askHelp":
    default:
      Icon = <AnnotationQuestionIcon />;
  }
  if (messageDetails.images[0]) {
    return <EntityPreviewCard image={messageDetails.images[0]} {...commonProps} />;
  }

  return <EntityPreviewCard icon={Icon} {...commonProps} />;
}

function CommunityPollPreview({
  entityId: pollId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();

  const pollQueries = usePollQueries();
  const {
    data: pollDetails,
    isPending: isPendingPollDetails,
    error: errorPollDetails,
  } = useQuery({
    ...pollQueries.details(pollId),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorPollDetails) {
    return <EntityPreviewCardError {...errorLabels} />;
  }

  if (isPendingPollDetails) {
    return <EntityPreviewCardSkeleton />;
  }

  return (
    <EntityPreviewCard
      href={readOnly ? undefined : routes.communityFeed.pollDetails({ slug, id: pollDetails.id })}
      onClick={readOnly ? undefined : onClick}
      title={pollDetails.title}
      icon={<HorizontalBarChart01Icon />}
      renderBadge={() => pollDetails.endsAt && <CountdownBadge endDate={parseISO(pollDetails.endsAt)} />}
    />
  );
}

function SurveyPreview({
  entityId: surveyId,
  shouldLoad,
  errorLabels,
  readOnly,
  onClick,
}: BaseEntityPreviewProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();

  const surveyQueries = useSurveyQueries();
  const {
    data: surveyDetails,
    isPending: isPendingSurveyDetails,
    error: errorSurveyDetails,
  } = useQuery({
    ...surveyQueries.details({
      surveyId,
      category: "other",
    }),
    retry: 1,
    enabled: shouldLoad,
  });

  if (errorSurveyDetails) {
    return <EntityPreviewCardWithCustomActionError {...errorLabels} />;
  }

  if (isPendingSurveyDetails) {
    return <EntityPreviewCardWithCustomActionSkeleton />;
  }

  const readOnlyOverride = readOnly || surveyDetails.canRespond !== "yes";

  return (
    <EntityPreviewCardWithCustomAction
      title={surveyDetails.title}
      subtitle={surveyDetails.description}
      renderBadge={() =>
        surveyDetails.endsAt ? <CountdownBadge endDate={parseISO(surveyDetails.endsAt)} /> : undefined
      }
      href={readOnlyOverride ? undefined : routes.surveys.respond({ slug, id: surveyDetails.id })}
      onClick={readOnlyOverride ? undefined : onClick}
      renderAction={() => (
        <Button fullWidth disabled={surveyDetails.canRespond !== "yes"}>
          {getCommunitySurveyCtaLabel(t, surveyDetails)}
        </Button>
      )}
    />
  );
}

type EntityPreviewProps = {
  entityType: SupportedDeepLinkEntityType;
  otherIds?: {
    folderId?: string;
  };
} & BaseEntityPreviewProps;

export function EntityPreview({ entityType, otherIds, ...baseProps }: EntityPreviewProps): React.ReactNode {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // In quick reply page, we will not be able to load the previews
  const isInQuickReplyPage = matchPath(
    {
      path: `${routes.quickReply.message({
        token: "",
      })}:token`,
    },
    pathname,
  );

  const errorLabels = {
    title: t("component.community-post.entity-preview.title.not-available"),
    subtitle: isInQuickReplyPage
      ? t("component.community-post.entity-preview.subtitle.require-auth")
      : t("component.community-post.entity-preview.subtitle.not-available"),
  };

  switch (entityType) {
    case "event":
      return <EventPreview {...{ ...baseProps, errorLabels }} />;
    case "group":
      return <GroupPreview {...{ ...baseProps, errorLabels }} />;
    case "service":
      return <ServicePreview {...{ ...baseProps, errorLabels }} />;
    case "bookableAsset":
      return <BookableAssetPreview {...{ ...baseProps, errorLabels }} />;
    case "document":
      return <PracticalAndMoreDocumentPreview folderId={otherIds?.folderId || ""} {...{ ...baseProps, errorLabels }} />;
    case "message":
      return <CommunityPostPreview {...{ ...baseProps, errorLabels }} />;
    case "poll":
      return <CommunityPollPreview {...{ ...baseProps, errorLabels }} />;
    case "survey":
      return <SurveyPreview {...{ ...baseProps, errorLabels }} />;
    default:
      return null;
  }
}
